import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  ActionIcon,
  Button,
  Grid,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IconArrowUpRight, IconAlertTriangle, IconPencil } from '@tabler/icons-react';
import { PageHeader } from '../components/ui/PageHeader';
import { KpiCard } from '../components/ui/KpiCard';
import { FormSection } from '../components/ui/FormSection';
import { ReportStatusBadge } from '../components/reports/ReportStatusBadge';
import { useAuth } from '../hooks/useAuth';
import { reportsApi } from '../services/reportsApi';
import { formatPeriod } from '../utils/reportPeriod';
import {
  ReportStatus,
  ReportTypeIdentifier,
  ReportTypeIdentifierLabels,
  type ReportInterval,
  type FormularAB,
  type FormularC,
  type FormularD,
  type EnvironmentalData,
  type AccidentData,
} from '../types/reports';

interface RecentReport {
  id: number;
  type: ReportTypeIdentifier;
  typeLabel: string;
  reportingPeriod: string;
  interval: ReportInterval;
  plantName: string;
  status: ReportStatus;
  updatedAt: string;
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Guten Tag';
  return 'Guten Abend';
}

function toRecent(
  report: FormularAB | FormularC | FormularD | EnvironmentalData | AccidentData,
  type: ReportTypeIdentifier,
): RecentReport {
  return {
    id: report.id,
    type,
    typeLabel: ReportTypeIdentifierLabels[type],
    reportingPeriod: report.reportingPeriod,
    interval: report.configuration.interval,
    plantName: report.plant?.name ?? '—',
    status: report.status,
    updatedAt: report.updatedAt ?? report.createdAt,
  };
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [reviewCount, setReviewCount] = useState<number | null>(null);
  const [recent, setRecent] = useState<RecentReport[] | null>(null);

  const loadData = useCallback(async () => {
    try {
      const { formularAB, formularC, formularD, environmental, accident } =
        await reportsApi.getAllReports();

      const unified: RecentReport[] = [
        ...formularAB.map((r) => toRecent(r, ReportTypeIdentifier.FORMULAR_AB)),
        ...formularC.map((r) => toRecent(r, ReportTypeIdentifier.FORMULAR_C)),
        ...formularD.map((r) => toRecent(r, ReportTypeIdentifier.FORMULAR_D)),
        ...environmental.map((r) => toRecent(r, ReportTypeIdentifier.ENVIRONMENTAL)),
        ...accident.map((r) => toRecent(r, ReportTypeIdentifier.ACCIDENT)),
      ];

      setOpenCount(
        unified.filter(
          (r) =>
            r.status === ReportStatus.DRAFT ||
            r.status === ReportStatus.IN_PROGRESS ||
            r.status === ReportStatus.REVISION_NEEDED,
        ).length,
      );
      setReviewCount(unified.filter((r) => r.status === ReportStatus.SUBMITTED).length);

      const sorted = [...unified].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      setRecent(sorted.slice(0, 5));
    } catch {
      setOpenCount(0);
      setReviewCount(0);
      setRecent([]);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Stack>
      <PageHeader
        title={
          <>
            {greeting()}, {user?.firstName ?? 'Melder'} <span aria-hidden>👋</span>
          </>
        }
        breadcrumb={{ label: 'Dashboard' }}
      />

      <Grid gap="md">
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <KpiCard
            link="/reports"
            label="Offene Meldungen"
            value={openCount === null ? <Loader size="sm" /> : openCount}
            accent={
              openCount && openCount > 0 ? (
                <Group gap={4} c="red.8">
                  <IconAlertTriangle size={10} stroke={1.5} />
                  <Text fz={10} lh={1.55}>
                    Fristablauf
                  </Text>
                </Group>
              ) : null
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <KpiCard
            link="/reports"
            label="In Prüfung"
            value={reviewCount === null ? <Loader size="sm" /> : reviewCount}
          />
        </Grid.Col>
      </Grid>

      <FormSection
        title="Zuletzt verwendet"
        rightSection={
          <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowUpRight size={14} />}
              onClick={() => navigate('/reports')}
              styles={{ label: { fontWeight: 700 } }}
          >
            Alle Meldungen anzeigen
          </Button>
        }
      >
        {recent === null ? (
          <Group justify="center" py="lg">
            <Loader />
          </Group>
        ) : recent.length === 0 ? (
          <Text c="vdzGray.7" ta="center" py="md">
            Noch keine Meldungen.
          </Text>
        ) : (
          <Table verticalSpacing="sm" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Meldung</Table.Th>
                <Table.Th>Zeitraum</Table.Th>
                <Table.Th>Werk</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th style={{ width: 48 }} aria-label="Aktion" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {recent.map((r) => (
                <Table.Tr
                  key={`${r.type}-${r.id}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/reports/${r.type}/${r.id}`)}
                >
                  <Table.Td>{r.typeLabel}</Table.Td>
                  <Table.Td>{formatPeriod(r.reportingPeriod, r.interval)}</Table.Td>
                  <Table.Td>{r.plantName}</Table.Td>
                  <Table.Td>
                    <ReportStatusBadge status={r.status} />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon variant="outline" color="vdzGray" title="Bearbeiten">
                      <IconPencil size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </FormSection>
    </Stack>
  );
}
